import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { Database } from "lucide-react";
import CollectionRecordsTable from "@/components/collections/collection-records-table";
import {
  setActiveCollectionIcon,
  setActiveCollectionTitle,
  useActiveCollection,
} from "@/components/collections/use-active-collection";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";

// `passthrough`: los filtros por valor (`value:<campo>`) viajan en la URL
// y se comparten con el kanban al cambiar de vista.
const collectionSearchSchema = z
  .object({
    page: z.number().optional().default(1),
    search: z.string().optional(),
    action: z.string().optional(),
  })
  .passthrough();

export const Route = createFileRoute("/_authenticated/collections/$collectionId/table")({
  validateSearch: collectionSearchSchema,
  component: CollectionPage,
});

/**
 * Registros de una colección propia (tabla + modal). Además del módulo
 * `collection_records` (layout), la colección puede restringirse a ciertos
 * roles: sin acceso, `getCollection` devuelve null.
 */
function CollectionPage() {
  const { collectionId } = Route.useParams();
  const searchParams = Route.useSearch();
  const active = useActiveCollection();
  // Nombre desde la lista del menu lateral (ya cargada): el titulo se ve al
  // instante al navegar, mientras llegan los campos de la coleccion.
  const navCollections = useQuery(api.companies.collections.queries.listAccessibleCollections);
  const navName = navCollections?.find((c) => c._id === collectionId)?.name;

  if (active === null) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 py-20 text-center">
        <Database className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">Colección no disponible</p>
        <p className="max-w-sm text-xs text-muted-foreground">
          No existe o tu rol no tiene acceso a ella.
        </p>
      </div>
    );
  }

  // Título de la tabla (ver use-active-collection.ts). Mientras carga, la
  // tabla se pinta igual (titulo, barra y cabecera) y solo el cuerpo espera.
  setActiveCollectionTitle(active?.collection.name ?? navName ?? "Colección");
  setActiveCollectionIcon(active?.collection.icon);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <CollectionRecordsTable
        key={collectionId}
        currentPage={searchParams.page}
        basePath={`/collections/${collectionId}/table`}
        searchParams={{ ...searchParams, collectionId }}
      />
    </div>
  );
}
